import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { ShieldCheck, Fingerprint, Copy } from "lucide-react";
import { toast } from "sonner";

const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");

export function TamperEvidentBadge({ manifestHash, lockedAt, testId = "tamper-evident-badge" }) {
  const [open, setOpen] = useState(false);
  if (!manifestHash) return null;

  const copy = async () => {
    try { await navigator.clipboard.writeText(manifestHash); toast.success("Fingerprint copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <>
      <button
        data-testid={testId}
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-full border border-[#BBF7D0] bg-[#F0FDF4] px-2.5 py-1 text-[11px] font-semibold text-[#166534] hover:bg-[#DCFCE7] transition-colors"
        title="This report is tamper-evident. Tap to learn more."
      >
        <ShieldCheck size={13} /> Tamper-evident ✓
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="tamper-evident-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-navy">
              <Fingerprint size={20} className="text-brand" /> Tamper-evident report
            </DialogTitle>
            <DialogDescription className="text-left leading-relaxed">
              When this report was locked{lockedAt ? ` on ${fmt(lockedAt)}` : ""}, we generated a unique
              cryptographic fingerprint (SHA-256) of every photo and combined them into a single
              "manifest" fingerprint below. If even one pixel of any photo were changed after locking,
              this fingerprint would no longer match — so a matching fingerprint shows the photos
              haven't been altered since.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">Manifest fingerprint (SHA-256)</p>
            <div className="mt-1.5 flex items-start gap-2 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-3">
              <code data-testid="tamper-manifest-hash" className="flex-1 break-all font-mono text-[11px] leading-relaxed text-[#0F172A]">{manifestHash}</code>
              <button onClick={copy} data-testid="tamper-copy-btn" className="text-brand hover:text-brand-hover shrink-0"><Copy size={15} /></button>
            </div>
          </div>

          <p className="text-xs text-[#94A3B8]">
            These fingerprints were generated when the report was locked; any alteration to the photos would change them. This is a local, self-contained check — no blockchain or third-party service is involved.
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
}
